'use client';

import AttributesSmallIconSvg from '@/assets/svgs/text-editor/attributes-small.svg';
import { inter500 } from '@/config/fonts';
import { Editor } from '@tiptap/react';
import classNames from 'classnames';
import { Fragment, HTMLAttributes, useEffect, useRef, useState } from 'react';
import useTextEditor from '../hooks/useTextEditor';
import { TEMPLATE_ATTRIBUTES, TemplateAttribute } from '../types';

interface AttributesDropdownProps extends HTMLAttributes<HTMLDivElement> {
  editor: Editor | null;
  isOpen?: boolean;
  shouldRemoveSlashAfterSelection?: boolean;
  onClose?: () => void;
}

const AttributesDropdown = ({
  className,
  editor,
  style,
  isOpen,
  shouldRemoveSlashAfterSelection,
  onClose,
}: AttributesDropdownProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { replaceSelectedText } = useTextEditor(editor);

  const groupedAttributes = TEMPLATE_ATTRIBUTES.reduce(
    (grouped, attribute) => {
      grouped[attribute.group] = grouped[attribute.group] || [];
      grouped[attribute.group].push(attribute);
      return grouped;
    },
    {} as Record<string, TemplateAttribute[]>
  );
  const groupNames = Object.keys(groupedAttributes);

  const [focusedIndex, setFocusedIndex] = useState(0);
  const [typedPhrase, setTypedPhrase] = useState('');
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setFocusedIndex(0);
  }, [isOpen]);

  // Clear the typed phase timeout
  useEffect(() => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }

    if (typedPhrase) {
      typingTimeout.current = setTimeout(() => {
        setTypedPhrase('');
      }, 750);
    }
  }, [typedPhrase]);

  useEffect(() => {
    const currentRef = ref.current;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!currentRef) return;

      const buttons = Array.from(currentRef.querySelectorAll('span[role="button"]'));

      if (event.key === 'ArrowDown') {
        setTypedPhrase('');
        setFocusedIndex((prevIndex) => (prevIndex + 1) % buttons.length);
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        setTypedPhrase('');
        setFocusedIndex((prevIndex) => (prevIndex - 1 + buttons.length) % buttons.length);
        event.preventDefault();
      } else if (event.key === 'Escape') {
        setTypedPhrase('');
        event.preventDefault();
        replaceSelectedText('');
      } else if (event.key === 'Enter') {
        setTypedPhrase('');

        const currentButton = buttons[focusedIndex] as HTMLElement;
        if (currentButton) {
          currentButton.click();
          event.preventDefault();
        }
      } else if (/^[a-zA-Z0-9]$/.test(event.key)) {
        // Handle character typing
        setTypedPhrase((prevPhrase) => prevPhrase + event.key.toLowerCase());

        const matchingIndex = buttons.findIndex((button) =>
          button.textContent
            ?.toLowerCase()
            .includes(typedPhrase + event.key.toLowerCase())
        );

        if (matchingIndex >= 0) {
          setFocusedIndex(matchingIndex);
        }
      }
    };

    currentRef?.addEventListener('keydown', handleKeyDown);

    const currentButton = currentRef?.querySelectorAll('span[role="button"]')[
      focusedIndex
    ] as HTMLElement;
    if (currentButton) {
      currentButton.focus();
    }

    return () => {
      currentRef?.removeEventListener('keydown', handleKeyDown);
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
      }
    };
  }, [focusedIndex, replaceSelectedText, typedPhrase]);

  return (
    <div
      tabIndex={0}
      className={classNames(
        'card dropdown-content card-compact bg-base-100 z-[1] max-h-52 w-[178px] flex-nowrap overflow-auto rounded-xl border-4 border-[rgba(0,0,0,0.04)] shadow dark:border dark:border-gray-800',
        className
      )}
      style={style}
      ref={ref}>
      <div className="card-body !px-0 !py-1">
        <ul tabIndex={0} className="menu p-0">
          {groupNames.map((groupName, idx) => {
            return (
              <Fragment key={idx}>
                <div
                  className={classNames(
                    'text-gray-default px-3 py-1 text-[12px]',
                    inter500.className
                  )}>
                  {groupName}
                </div>
                {groupedAttributes[groupName].map((attribute, idx) => {
                  return (
                    <li key={idx}>
                      <span
                        className="flex gap-2 rounded-none py-1.5 pr-0 pl-3 text-[13px]"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          const attributeHtml = `<text-editor-attribute key="${attribute.key}" text="${attribute.label}"></text-editor-attribute>`;

                          if (shouldRemoveSlashAfterSelection && editor) {
                            // The slash was inserted as a literal text character
                            // immediately before the cursor. Delete it and insert
                            // the attribute in a single ProseMirror transaction so
                            // positions stay valid regardless of other inline
                            // nodes already in the document.
                            const slashPos = editor.state.selection.from - 1;
                            editor
                              .chain()
                              .focus()
                              .deleteRange({ from: slashPos, to: slashPos + 1 })
                              .insertContent(attributeHtml, { updateSelection: true })
                              .run();
                          } else {
                            replaceSelectedText(attributeHtml);
                          }

                          onClose?.();
                        }}>
                        <AttributesSmallIconSvg />
                        {attribute.label}
                      </span>
                    </li>
                  );
                })}
              </Fragment>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default AttributesDropdown;
